import React, { Component, ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode | ((error: Error, resetError: () => void) => ReactNode)
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  level?: 'route' | 'component'
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component to catch and handle React errors gracefully.
 *
 * Supports two levels:
 * - 'route': Full-page error display for route-level failures
 * - 'component': Inline card-style display for component-level failures
 *
 * Features:
 * - Custom fallback UI support
 * - Error callback for logging/monitoring
 * - Dark mode support
 * - Reset functionality to recover from errors
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Detect stale chunk errors after a deployment and force a full reload.
    // This happens when the app is left open in a tab and a new deploy
    // changes the hashed asset filenames.
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk')

    if (isChunkError) {
      const reloadKey = 'chunk-reload-attempted'
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1')
        window.location.reload()
        return
      }
      // Already tried once this session, fall through to error UI
      sessionStorage.removeItem(reloadKey)
    }

    // Call optional error callback for logging/monitoring
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render(): ReactNode {
    const { hasError, error } = this.state
    const { children, fallback, level = 'component' } = this.props

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(error, this.resetError)
        }
        return fallback
      }

      // Default fallback UI
      const isRoute = level === 'route'
      const isDevelopment = import.meta.env.MODE === 'development'

      return (
        <div
          className={cn(
            'flex items-center justify-center',
            isRoute ? 'min-h-screen p-4' : 'min-h-[300px] p-4'
          )}
        >
          <div
            className={cn(
              'rounded-lg border bg-white p-8 shadow-sm dark:bg-gray-800 dark:border-gray-700',
              isRoute ? 'max-w-md w-full' : 'max-w-sm w-full'
            )}
          >
            <div className="flex flex-col items-center text-center">
              {/* Error Icon */}
              <div
                className={cn(
                  'rounded-full bg-red-100 p-3 dark:bg-red-900/20',
                  isRoute ? 'mb-4' : 'mb-3'
                )}
              >
                <AlertTriangle
                  size={isRoute ? 32 : 24}
                  className={cn(
                    'text-red-600 dark:text-red-400'
                  )}
                />
              </div>

              {/* Error Heading */}
              <h2
                className={cn(
                  'font-semibold text-gray-900 dark:text-white',
                  isRoute ? 'text-xl mb-2' : 'text-lg mb-2'
                )}
              >
                Something went wrong
              </h2>

              {/* Error Message (development only) */}
              {isDevelopment && (
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 font-mono break-all">
                  {error.message}
                </p>
              )}

              {/* Production Message */}
              {!isDevelopment && (
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  An unexpected error occurred. Please try again.
                </p>
              )}

              {/* Try Again Button */}
              <button
                onClick={this.resetError}
                className={cn(
                  'rounded-md bg-primary px-4 py-2 text-sm font-medium text-white',
                  'hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary',
                  'focus:ring-offset-2 dark:focus:ring-offset-gray-800',
                  'transition-colours duration-200'
                )}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )
    }

    return children
  }
}

/**
 * Higher-Order Component wrapper for ErrorBoundary.
 *
 * Usage:
 * ```tsx
 * const SafeComponent = withErrorBoundary(MyComponent, {
 *   level: 'component',
 *   onError: (error) => console.error(error)
 * })
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  )

  // Preserve component name for debugging
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`

  return WrappedComponent
}

// Named export
export { ErrorBoundary }

// Default export
export default ErrorBoundary
